import {
  fieldProblemReasonLabels,
  missionDurations,
  segmentTrack,
  type FieldItemStatus,
  type FieldMissionItem,
  type OperatorMissionView,
} from '@groupe-reca/contracts'
import { useQueryClient } from '@tanstack/react-query'
import {
  ArrowDownUp,
  ArrowLeft,
  Columns2,
  Flag,
  Handshake,
  List,
  LocateFixed,
  LocateOff,
  Pause,
  Settings,
  SlidersHorizontal,
  Play,
  RefreshCw,
  Route as RouteIcon,
  TriangleAlert,
  WifiOff,
  X,
} from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router'
import { appDependencies } from '@/app/dependencies'
import logo from '@/assets/logo.svg'
import { useOnline } from '@/shared/hooks/useOnline'
import { runCommand } from '../application/commands'
import { missionKeys, useMissionTrack, useMissionView } from '../application/missionQueries'
import { operationKeys, useReinforcementOffers } from '../application/operationsQueries'
import { useOperator } from '../application/OperatorServices'
import { diagnoseDetection, serverProfileOf } from '../application/detectionSource'
import {
  overrideFor,
  readProfileOverride,
  writeProfileOverride,
  type ProfileOverride,
} from '../application/profileOverride'
import { useDetection } from '../application/useDetection'
import { demoProfile } from '@/features/detection/domain/profile'
import { PositionTracker, type TrackerState } from '../application/tracking/PositionTracker'
import { useWakeLock } from '../application/useWakeLock'
import { formatDuration, formatEngineHours, formatTime, hasCoordinates, itemAddress, serverMessage } from '../domain/fieldText'
import { AddressChoice } from './components/AddressChoice'
import { DetectionDebugPanel } from './components/DetectionDebugPanel'
import { metersBetween } from '@/features/detection/domain/geometry'
import { travelEstimate } from '../domain/map/fieldMeasures'
import { approachOf, guideAvailability, nextLiaison, routeRemaining } from '../domain/map/routeGuide'
import { streetSide } from '../domain/map/streetSide'
import { useStableMap } from './useStableMap'
import { ProfileWindow } from './components/ProfileWindow'
import { EndMissionDialog } from './components/EndMissionDialog'
import { FieldBanners } from './components/FieldBanners'
import { FocusCard, type PanelVariant } from './components/FocusCard'
import { HelpAvailability } from './components/HelpAvailability'
import { HelpButton } from './components/HelpButton'
import { HoldButton, type HoldPhase } from './components/HoldButton'
import { ItemSheet, type ProblemDraft } from './components/ItemSheet'
import { MissionCard } from './components/MissionCard'
import { MissionMap, type MapDetection, type RotationMode } from './components/MissionMap'
import { ReinforcementPanel } from './components/ReinforcementPanel'
import { SettingsDialog } from './components/SettingsDialog'
import { focusOf, nextAfter } from './focus'
import { readOperatorSettings, writeOperatorSettings, type OperatorSettings } from './operatorSettings'
import { destinationOf, sortAnchor, sortResidences, type ResidenceSort } from './residenceOrder'
import { useSuggestedPath } from './useSuggestedPath'

interface CardMessage {
  kind: 'short' | 'error'
  text: string
}

const filters: { key: FieldItemStatus; label: string }[] = [
  { key: 'WAITING', label: 'À faire' },
  { key: 'PROBLEM', label: 'Problèmes' },
  { key: 'COMPLETED', label: 'Terminées' },
]

const trackPreferenceKey = 'reca-operator-v3.show-track'
const suggestedPreferenceKey = 'reca-operator-v3.show-suggested-path'
const emptyDraft: ProblemDraft = { reasonCode: null, reasonText: '', note: '' }

function readTrackPreference(): boolean {
  try {
    return window.localStorage.getItem(trackPreferenceKey) === '1'
  } catch {
    return false
  }
}

function readSuggestedPreference(): boolean {
  try {
    return window.localStorage.getItem(suggestedPreferenceKey) === '1'
  } catch {
    return false
  }
}

// Diagnostic de calibration TEMPORAIRE (lot O7) : jamais en production, affiché par défaut ailleurs.
const debugAllowed = appDependencies.config.calibrationTools
const debugPreferenceKey = 'reca-operator-v3.detection-debug'

// Drapeaux et zones (capture, travail, sortie) sur la carte : bouton « Détection », indépendant du panneau Diag.
const zonesPreferenceKey = 'reca-operator-v3.detection-zones'

function readCalibrationPreference(key: string): boolean {
  if (!debugAllowed) return false
  try {
    return window.localStorage.getItem(key) !== '0'
  } catch {
    return true
  }
}

function writeCalibrationPreference(key: string, value: boolean): void {
  try {
    window.localStorage.setItem(key, value ? '1' : '0')
  } catch {
    // préférence non mémorisée
  }
}

function without<T>(record: Record<string, T>, key: string): Record<string, T> {
  return Object.fromEntries(Object.entries(record).filter(([entryKey]) => entryKey !== key))
}

function useTracker(missionId: string, active: boolean): { state: TrackerState; tracker: PositionTracker } {
  const { store, sync } = useOperator()
  const tracker = useMemo(
    () => new PositionTracker(missionId, appDependencies.geolocation, store, sync),
    [missionId, store, sync],
  )
  const [state, setState] = useState<TrackerState>(tracker.snapshot)
  useEffect(() => tracker.subscribe(setState), [tracker])
  useEffect(() => {
    if (!active) {
      tracker.stop(null)
      return
    }
    if (!document.hidden) tracker.resume()
    const onVisibility = () => {
      // Page masquée (autre application, écran éteint) : le navigateur ne garantit plus rien, on le consigne.
      if (document.hidden) tracker.stop('APP_HIDDEN')
      else tracker.resume()
    }
    document.addEventListener('visibilitychange', onVisibility)
    return () => {
      document.removeEventListener('visibilitychange', onVisibility)
      tracker.stop(null)
    }
  }, [tracker, active])
  return { state, tracker }
}

interface GpsReading {
  tone: 'ok' | 'warn' | 'idle'
  label: string
  detail: string
}

function gpsReading(state: TrackerState, accuracyM: number | null, imprecise: boolean): GpsReading | null {
  const precision = accuracyM === null ? '' : ` ± ${String(Math.round(accuracyM))} m`
  switch (state.status) {
    case 'ok':
      if (imprecise) {
        return {
          tone: 'warn',
          label: `GPS imprécis${precision}`,
          detail:
            'La précision dépasse le seuil admis : la détection automatique est suspendue, aucune adresse n’est déduite. La position reste enregistrée. Placez la tablette à découvert, près du pare-brise.',
        }
      }
      return {
        tone: 'ok',
        label: accuracyM !== null ? `GPS fiable${precision}` : 'GPS',
        detail: `Position reçue${state.lastRecordedAt !== null ? `, dernier relevé à ${formatTime(state.lastRecordedAt)}` : ''}.`,
      }
    case 'pending':
      return { tone: 'idle', label: 'Localisation…', detail: 'En attente d’une première position.' }
    case 'stale':
      return {
        tone: 'warn',
        label: 'GPS ancien',
        detail:
          'La dernière position est trop ancienne : la détection automatique ne s’en sert pas. Vérifiez que la localisation est active et que la tablette voit le ciel.',
      }
    case 'denied':
      return {
        tone: 'warn',
        label: 'GPS refusé',
        detail:
          'La localisation est refusée : votre position n’est ni affichée ni enregistrée, aucune détection automatique. Autorisez la localisation pour ce site, puis réessayez. Fiches et confirmations restent disponibles.',
      }
    case 'unavailable':
      return {
        tone: 'warn',
        label: 'GPS indisponible',
        detail:
          'La tablette ne fournit pas de position : aucune détection automatique. Vérifiez que la localisation est activée dans les réglages Android.',
      }
    case 'unsupported':
      return {
        tone: 'warn',
        label: 'GPS non pris en charge',
        detail: 'Ce navigateur ne donne pas accès à la localisation : aucune détection automatique.',
      }
    case 'idle':
      return null
  }
}

/** Indicateur GPS compact : distinct du réseau, il ouvre l'explication et l'action corrective. */
function GpsIndicator({
  state,
  accuracyM,
  imprecise,
  onRetry,
}: {
  state: TrackerState
  accuracyM: number | null
  imprecise: boolean
  onRetry: () => void
}) {
  const [open, setOpen] = useState(false)
  const reading = gpsReading(state, accuracyM, imprecise)
  if (!reading) return null
  const Icon = reading.tone === 'warn' ? LocateOff : LocateFixed
  return (
    <span className="op-gps">
      <button
        type="button"
        className={`field-pill op-gps__pill${reading.tone === 'ok' ? ' field-pill--ok' : reading.tone === 'warn' ? ' field-pill--warn' : ''}`}
        aria-expanded={open}
        aria-label={`${reading.label} : explication`}
        onClick={() => {
          setOpen((value) => !value)
        }}
      >
        <Icon aria-hidden="true" /> <span role="status">{reading.label}</span>
      </button>
      {open ? (
        <span className="op-gps__pop" role="dialog" aria-label="État du GPS">
          <strong>{reading.label}</strong>
          <span>{reading.detail}</span>
          <span className="op-gps__actions">
            {state.status === 'denied' || state.status === 'unavailable' || state.status === 'stale' ? (
              <button
                type="button"
                className="field-button field-button--outline"
                onClick={() => {
                  onRetry()
                  setOpen(false)
                }}
              >
                Réessayer
              </button>
            ) : null}
            <button
              type="button"
              className="field-button field-button--outline"
              onClick={() => {
                setOpen(false)
              }}
            >
              Fermer
            </button>
          </span>
        </span>
      ) : null}
    </span>
  )
}

function transferNotice(item: FieldMissionItem, ownerName: string | null): string {
  return `${itemAddress(item.snapshot)} : prise en charge par ${ownerName ?? 'une autre mission'}. Votre compte rendu est gardé pour le bureau, rien n’est appliqué ici.`
}

function MissionSummary({ view, onHome }: { view: OperatorMissionView; onHome: () => void }) {
  const { mission } = view
  const durations = missionDurations({
    startedAt: mission.startedAt,
    endedAt: mission.endedAt,
    pausedSeconds: mission.pausedSeconds,
    startEngineHours: mission.startEngineHours,
    endEngineHours: mission.endEngineHours,
  })
  const unresolved = mission.progress.waiting + mission.progress.problem > 0
  return (
    <main className="op-screen op-screen--center">
      <section className="op-card op-summary" aria-label="Fin de sortie">
        <span className="field-eyebrow">Mission n° {mission.number ?? '—'}</span>
        <h1>
          {mission.status === 'CANCELLED'
            ? mission.cancelKind === 'ACCIDENTAL_START'
              ? 'Démarrage annulé'
              : 'Mission annulée par le bureau'
            : unresolved
              ? 'Sortie terminée avec résidences non résolues'
              : 'Sortie terminée'}
        </h1>
        {mission.cancelKind === 'ACCIDENTAL_START' ? (
          <p className="op-muted">
            Annulée — démarrage accidentel. Elle ne compte ni dans les heures ni dans les performances; vos résidences
            restent à faire.
          </p>
        ) : mission.cancelledReason ? (
          <p className="op-muted">Motif : {mission.cancelledReason}</p>
        ) : null}
        <dl className="op-summary__grid">
          <div>
            <dt>Terminées</dt>
            <dd>{mission.progress.completed}</dd>
          </div>
          <div>
            <dt>À faire</dt>
            <dd>{mission.progress.waiting}</dd>
          </div>
          <div>
            <dt>Problèmes</dt>
            <dd>{mission.progress.problem}</dd>
          </div>
          <div>
            <dt>Durée de la sortie</dt>
            <dd>{formatDuration(durations.elapsedSeconds)}</dd>
          </div>
          <div>
            <dt>Pauses</dt>
            <dd>{formatDuration(durations.pausedSeconds)}</dd>
          </div>
          <div>
            <dt>Heures moteur</dt>
            <dd>{durations.engineHours === null ? '—' : formatEngineHours(durations.engineHours)}</dd>
          </div>
        </dl>
        {unresolved && mission.status === 'COMPLETED' ? (
          <p className="op-muted">
            Les résidences restantes ne sont pas comptées comme réalisées : le bureau les voit et les traitera.
          </p>
        ) : null}
        {(mission.progress.transferred ?? 0) > 0 ? (
          <p className="op-muted">
            {mission.progress.transferred} résidence(s) prise(s) en charge par une autre mission (renfort).
          </p>
        ) : null}
        {/* La sortie est fermée, l'opérateur reste connecté : il peut se déclarer disponible pour un renfort. */}
        <HelpAvailability />
        <button type="button" className="op-start-button" onClick={onHome}>
          <ArrowLeft aria-hidden="true" /> Retour à l’accueil
        </button>
      </section>
    </main>
  )
}

export function FieldPage() {
  const { missionId = '' } = useParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { userId, repository, store, syncState, flush } = useOperator()
  const view = useMissionView(missionId)
  const online = useOnline()
  // Résidences prises en charge par une autre mission (renfort) : hors de la liste, de la carte et du moteur.
  const liveView = useMemo(
    () => (view.data ? { ...view.data, items: view.data.items.filter((item) => !item.transfer) } : undefined),
    [view.data],
  )
  const transferredCount = (view.data?.items.length ?? 0) - (liveView?.items.length ?? 0)
  const mission = view.data?.mission
  const inProgress = mission?.status === 'IN_PROGRESS'
  const { state: gps, tracker } = useTracker(missionId, inProgress)
  const [override, setOverride] = useState<ProfileOverride | null>(() => (debugAllowed ? readProfileOverride() : null))
  const [profileOpen, setProfileOpen] = useState(false)
  const serverProfile = view.data ? serverProfileOf(view.data) : null
  // Une calibration faite sur un autre profil du serveur (ex. passage en AUTO) est ignorée.
  const activeOverride = overrideFor(override, serverProfile)
  const detection = useDetection(liveView, appDependencies.geolocation, appDependencies.detection, activeOverride)
  const wakeLock = useWakeLock(appDependencies.wakeLock, inProgress)
  const [filter, setFilter] = useState<FieldItemStatus>('WAITING')
  const [phases, setPhases] = useState<Record<string, HoldPhase>>({})
  const [messages, setMessages] = useState<Record<string, CardMessage>>({})
  const [selectedId, setSelectedId] = useState<string | null>(null)
  // Fiche ouverte depuis « Problème » : le signalement à motif obligatoire s'ouvre directement.
  const [sheetProblem, setSheetProblem] = useState(false)
  const [drafts, setDrafts] = useState<Record<string, ProblemDraft>>({})
  const [problemState, setProblemState] = useState<{ status: 'idle' | 'saving' | 'error'; message?: string }>({
    status: 'idle',
  })
  const [showTrack, setShowTrack] = useState(readTrackPreference)
  // Plus proche par défaut à chaque ouverture : l'ordre est suggéré, jamais imposé. « Ordre de la route » au choix.
  const [sort, setSort] = useState<ResidenceSort>('NEAREST')
  const [showSuggested, setShowSuggested] = useState(readSuggestedPreference)
  const [notice, setNotice] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [endOpen, setEndOpen] = useState(false)
  const [settings, setSettings] = useState<OperatorSettings>(readOperatorSettings)
  const [settingsOpen, setSettingsOpen] = useState(false)
  const variant: PanelVariant = settings.panelStyle
  const [listOpen, setListOpen] = useState(false)
  const [problemFocusId, setProblemFocusId] = useState<string | null>(null)
  const [dismissedDecision, setDismissedDecision] = useState<string | null>(null)
  const [showDebug, setShowDebug] = useState(() => readCalibrationPreference(debugPreferenceKey))
  // Bouton Diag masqué dans les Paramètres : le diagnostic l'est aussi (sinon impossible à refermer).
  const diagnosing = settings.showDiagButton && showDebug
  const [showZones, setShowZones] = useState(() => readCalibrationPreference(zonesPreferenceKey))
  // Même règle pour le bouton Détection : masqué, les drapeaux et les zones le sont aussi.
  const showingZones = settings.showDetectionButton && showZones
  const trackQuery = useMissionTrack(missionId, showTrack)
  const noticeTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  const viewKey = missionKeys.view(userId, missionId)
  const items = useMemo(() => liveView?.items ?? [], [liveView])
  const offers = useReinforcementOffers()
  const claimableOffers = offers.data?.offers.filter((offer) => offer.canClaim).length ?? 0
  const [reinforcementOpen, setReinforcementOpen] = useState(false)
  const missionStatus = mission?.status
  // `canClaim` dépend de la mission (en cours, en pause, fermée) : offres relues à chaque changement d'état.
  useEffect(() => {
    if (missionStatus) void queryClient.invalidateQueries({ queryKey: operationKeys.offers(userId) })
  }, [missionStatus, missionId, queryClient, userId])
  const counts = mission?.progress ?? { total: 0, completed: 0, problem: 0, waiting: 0 }
  const snapshot = detection?.snapshot ?? null
  const anchor = sortAnchor(gps.position)
  const anchorLat = anchor?.lat ?? null
  const anchorLng = anchor?.lng ?? null
  const ordered = useMemo(
    () => sortResidences(items, sort, anchorLat === null || anchorLng === null ? null : { lat: anchorLat, lng: anchorLng }),
    [items, sort, anchorLat, anchorLng],
  )
  const flags = detection?.setup.flags ?? null
  const focus = useMemo(
    () => focusOf(items, snapshot, flags, problemFocusId, ordered),
    [items, snapshot, flags, problemFocusId, ordered],
  )
  const focusId = focus?.item.id ?? null
  const focusTone = focus?.tone ?? null
  const mapDetection = useMemo<MapDetection | null>(() => {
    if (!snapshot) return null
    const { view: engine, lastFix } = snapshot
    return {
      activeId: engine.active?.itemId ?? null,
      activeChip: focusTone === 'capture' ? 'verrouillée' : 'en cours',
      approachIds: Object.entries(engine.statuses).flatMap(([itemId, status]) => (status === 'APPROACH' ? [itemId] : [])),
      candidateIds: engine.ambiguity ?? [],
      uncertaintyM:
        engine.gps.status === 'SUSPENDED' && engine.gps.reason === 'INACCURATE' ? (lastFix?.accuracyM ?? null) : null,
    }
  }, [snapshot, focusTone])
  // Carte stable : parcours figé dans la mission (jamais recalculé ici), résidence confirmée par le moteur, pause.
  const [openedAt] = useState(() => Date.now())
  const routeGuide = mission?.routeGuide
  const guide = useMemo(() => guideAvailability(routeGuide, openedAt), [routeGuide, openedAt])
  const activeId = snapshot?.view.active?.itemId ?? null
  const work = useMemo(() => {
    const item = activeId === null ? undefined : items.find((candidate) => candidate.id === activeId)
    if (!item) return null
    const flag = item.snapshot.flag
    if (flag) return { itemId: item.id, entry: { lat: flag.latitude, lng: flag.longitude } }
    const { latitude, longitude } = item.snapshot
    return latitude === null || longitude === null ? null : { itemId: item.id, entry: { lat: latitude, lng: longitude } }
  }, [activeId, items])
  // Paramètre « Carte stable à la résidence » désactivé : la carte continue de suivre le tracteur (la pause reste figée).
  const [mapRotation, setMapRotation] = useState<{ rotation: RotationMode; following: boolean } | null>(null)
  const stable = useStableMap({
    operator: gps,
    guide,
    work: settings.stabilizeAtResidence ? work : null,
    paused: mission?.status === 'PAUSED',
  })
  // Fiche cible : côté de la rue (sens d'approche prévu, gardé pendant les manœuvres) et estimation qualifiée.
  const travelling = stable.camera.mode === 'TRAVEL'
  const reversed = travelling && stable.direction === 'BACKWARD'
  const fixFresh = !stable.camera.uncertain
  const focusApproach = useMemo(() => {
    const item = focus?.item
    if (!item) return null
    const { latitude, longitude } = item.snapshot
    const service = latitude === null || longitude === null ? null : { lat: latitude, lng: longitude }
    const path = guide.kind === 'READY' ? guide.path : null
    const approach = path ? approachOf(path, item.serviceLocationId) : null
    const side = approach && service ? streetSide(approach, service, reversed) : null
    const route =
      guide.kind === 'READY' && stable.along !== null && fixFresh
        ? routeRemaining(guide.path, guide.polyline, stable.along, item.serviceLocationId)
        : null
    const destination = destinationOf(item)
    const direct = fixFresh && gps.position && destination ? metersBetween(gps.position, destination) : null
    return {
      side,
      estimate: travelEstimate({ routeMeters: route?.meters ?? null, routeSeconds: route?.seconds ?? null, directMeters: direct }),
    }
  }, [focus, guide, reversed, stable.along, fixFresh, gps.position])
  const liaison = useMemo(
    () => nextLiaison(routeGuide, items.filter((item) => item.status === 'WAITING').length),
    [routeGuide, items],
  )
  const debugRadii = detection?.setup.profile ?? demoProfile
  const debugFlags = useMemo(() => {
    if (!showingZones) return null
    const flags = items.flatMap((item) =>
      item.status === 'WAITING' && item.snapshot.flag
        ? [
            {
              id: item.id,
              flag: { lat: item.snapshot.flag.latitude, lng: item.snapshot.flag.longitude },
              marker: hasCoordinates(item.snapshot)
                ? { lat: item.snapshot.latitude ?? 0, lng: item.snapshot.longitude ?? 0 }
                : null,
            },
          ]
        : [],
    )
    return { flags, radii: debugRadii }
  }, [showingZones, items, debugRadii])
  // La résidence mise en avant n'est pas répétée dans la liste.
  const visible = useMemo(
    () => ordered.filter((item) => item.status === filter && item.id !== focusId),
    [ordered, filter, focusId],
  )
  // Trajet suggéré : vers la résidence mise en avant, ou la suivante pendant le travail sur place.
  const suggestionTarget = useMemo(() => {
    const target =
      focus && (focus.tone === 'next' || focus.tone === 'approach') ? focus.item : nextAfter(items, focus?.item.id ?? null, ordered)
    const point = target ? destinationOf(target) : null
    if (target && point) return { id: target.id, point }
    // Plus rien à faire : liaison vers la route suivante ou retour au dépôt.
    return liaison ? { id: `liaison:${liaison.kind}`, point: liaison.point } : null
  }, [focus, items, ordered, liaison])
  // Street View de la carte : la maison de la résidence mise en avant (position de l'adresse, pas le drapeau).
  const streetTarget = useMemo(() => {
    const item = focus?.item
    const lat = item?.snapshot.latitude ?? null
    const lng = item?.snapshot.longitude ?? null
    if (!item || lat === null || lng === null) return null
    return {
      id: item.id,
      point: { lat, lng },
      address: itemAddress(item.snapshot),
    }
  }, [focus])
  const suggested = useSuggestedPath(appDependencies.config.mapboxToken, showSuggested && inProgress, gps.position, suggestionTarget)
  const selected = selectedId ? (items.find((item) => item.id === selectedId) ?? null) : null
  const trackSegments = useMemo(
    () => (showTrack && trackQuery.data ? segmentTrack(trackQuery.data.points, [...trackQuery.data.pauses, ...trackQuery.data.gaps]) : null),
    [showTrack, trackQuery.data],
  )

  const showNotice = useCallback((text: string, ms = 5000) => {
    setNotice(text)
    if (noticeTimer.current) clearTimeout(noticeTimer.current)
    noticeTimer.current = setTimeout(() => {
      setNotice(null)
    }, ms)
  }, [])

  useEffect(
    () => () => {
      if (noticeTimer.current) clearTimeout(noticeTimer.current)
    },
    [],
  )

  const setMessage = useCallback((itemId: string, message: CardMessage | null, ttl?: number) => {
    setMessages((current) => (message ? { ...current, [itemId]: message } : without(current, itemId)))
    if (message && ttl) {
      setTimeout(() => {
        setMessages((current) => (current[itemId] === message ? without(current, itemId) : current))
      }, ttl)
    }
  }, [])

  /** État serveur confirmé : la résidence change d'état dans la liste et sur la carte. */
  const applyItemStatus = useCallback(
    (itemId: string, status: FieldItemStatus) => {
      queryClient.setQueryData<OperatorMissionView>(viewKey, (current) => {
        if (!current) return current
        const nextItems = current.items.map((item) => (item.id === itemId ? { ...item, status } : item))
        const own = nextItems.filter((item) => !item.transfer)
        return {
          ...current,
          items: nextItems,
          mission: {
            ...current.mission,
            progress: {
              total: own.length,
              completed: own.filter((item) => item.status === 'COMPLETED').length,
              problem: own.filter((item) => item.status === 'PROBLEM').length,
              waiting: own.filter((item) => item.status === 'WAITING').length,
              transferred: nextItems.length - own.length,
            },
          },
        }
      })
      void queryClient.invalidateQueries({ queryKey: viewKey })
    },
    [queryClient, viewKey],
  )

  function finishSuccess(item: FieldMissionItem) {
    setPhases((current) => ({ ...current, [item.id]: 'success' }))
    setTimeout(() => {
      applyItemStatus(item.id, 'COMPLETED')
      setPhases((current) => without(current, item.id))
      setSelectedId((current) => (current === item.id ? null : current))
      showNotice(`Travail enregistré : ${itemAddress(item.snapshot)}`)
    }, 900)
  }

  async function complete(item: FieldMissionItem) {
    if (phases[item.id]) return
    setMessage(item.id, null)
    setPhases((current) => ({ ...current, [item.id]: 'confirming' }))
    const outcome = await runCommand(store, `complete:${item.id}`, (idempotencyKey) =>
      repository.complete({ idempotencyKey, itemId: item.id }),
    )
    if (outcome.kind === 'ok' && outcome.value.conflict) {
      // Prise en charge par une autre mission entre-temps : rien n'est appliqué, le bureau voit le conflit.
      setPhases((current) => without(current, item.id))
      showNotice(transferNotice(item, outcome.value.conflict.ownerName), 10_000)
      void view.refetch()
      return
    }
    if (outcome.kind === 'ok') {
      finishSuccess(item)
      return
    }
    if (outcome.kind === 'uncertain') {
      // Résultat inconnu : relire avant de décider; la même clé resservira au prochain maintien.
      const fresh = await view.refetch()
      if (fresh.data?.items.find((candidate) => candidate.id === item.id)?.status === 'COMPLETED') {
        await store.clearKey(`complete:${item.id}`)
        finishSuccess(item)
        return
      }
    }
    setPhases((current) => without(current, item.id))
    setMessage(item.id, {
      kind: 'error',
      text:
        outcome.kind === 'refused'
          ? serverMessage(outcome.code)
          : 'Travail non confirmé par le serveur. La résidence reste à faire : maintenez de nouveau le bouton.',
    })
    if (outcome.kind === 'refused') void view.refetch()
  }

  async function reportProblem(item: FieldMissionItem, draft: ProblemDraft): Promise<boolean> {
    if (!draft.reasonCode) return false
    setProblemState({ status: 'saving' })
    const outcome = await runCommand(store, `problem:${item.id}`, (idempotencyKey) =>
      repository.reportProblem({
        idempotencyKey,
        itemId: item.id,
        reasonCode: draft.reasonCode ?? 'OTHER',
        ...(draft.reasonText.trim() ? { reasonText: draft.reasonText.trim() } : {}),
        ...(draft.note.trim() ? { note: draft.note.trim() } : {}),
      }),
    )
    if (outcome.kind === 'ok' && outcome.value.conflict) {
      setProblemState({ status: 'idle' })
      setSelectedId(null)
      showNotice(transferNotice(item, outcome.value.conflict.ownerName), 10_000)
      void view.refetch()
      return true
    }
    if (outcome.kind === 'ok') {
      if (detection?.snapshot.view.active?.itemId === item.id) setProblemFocusId(item.id)
      setProblemState({ status: 'idle' })
      setDrafts((current) => without(current, item.id))
      applyItemStatus(item.id, 'PROBLEM')
      showNotice(`Problème enregistré : ${itemAddress(item.snapshot)}`)
      return true
    }
    setProblemState({
      status: 'error',
      message:
        outcome.kind === 'refused'
          ? serverMessage(outcome.code)
          : 'Le serveur n’a pas confirmé. Votre saisie est conservée : réessayez (la même demande sera renvoyée).',
    })
    if (outcome.kind === 'refused') void view.refetch()
    return false
  }

  async function togglePause() {
    if (!mission) return
    setBusy(true)
    const pausing = mission.status === 'IN_PROGRESS'
    const scope = `${pausing ? 'pause' : 'resume'}:${mission.id}`
    const outcome = await runCommand(store, scope, (idempotencyKey) =>
      pausing
        ? repository.pause({ idempotencyKey, missionId: mission.id })
        : repository.resume({ idempotencyKey, missionId: mission.id }),
    )
    setBusy(false)
    await view.refetch()
    if (outcome.kind !== 'ok')
      showNotice(
        outcome.kind === 'refused' ? serverMessage(outcome.code) : 'Le serveur n’a pas répondu. Réessayez.',
        8000,
      )
  }

  function changeSettings(next: OperatorSettings) {
    if (next.panelStyle !== settings.panelStyle) setListOpen(false)
    if (!next.showProfileButton) setProfileOpen(false)
    setSettings(next)
    writeOperatorSettings(next)
  }

  function toggleVariant() {
    changeSettings({ ...settings, panelStyle: variant === '1a' ? '1b' : '1a' })
  }

  function toggleSuggested() {
    setShowSuggested((value) => {
      try {
        window.localStorage.setItem(suggestedPreferenceKey, value ? '0' : '1')
      } catch {
        // préférence non mémorisée
      }
      return !value
    })
  }

  function toggleTrack() {
    setShowTrack((value) => {
      try {
        window.localStorage.setItem(trackPreferenceKey, value ? '0' : '1')
      } catch {
        // préférence non mémorisée
      }
      return !value
    })
  }

  function toggleDebug(value: boolean) {
    setShowDebug(value)
    writeCalibrationPreference(debugPreferenceKey, value)
  }

  function toggleZones(value: boolean) {
    setShowZones(value)
    writeCalibrationPreference(zonesPreferenceKey, value)
  }

  if (view.isPending) {
    return (
      <main className="op-screen op-screen--center">
        <p className="op-muted">Chargement de la mission…</p>
      </main>
    )
  }
  // Une relecture échouée (hors ligne) garde l'écran et ses données : la page d'erreur n'existe que sans données.
  if (!mission) {
    return (
      <main className="op-screen op-screen--center">
        <div className="field-error" role="alert">
          <strong>La mission n’a pas pu être chargée</strong>
          <p>Le serveur n’a pas répondu. Vos données déjà enregistrées ne sont pas touchées.</p>
          <button type="button" className="field-button field-button--primary" onClick={() => void view.refetch()}>
            <RefreshCw aria-hidden="true" /> Réessayer
          </button>
          <button type="button" className="field-button field-button--outline" onClick={() => void navigate('/')}>
            Accueil
          </button>
        </div>
      </main>
    )
  }
  if (mission.status === 'COMPLETED' || mission.status === 'CANCELLED') {
    return (
      <MissionSummary
        view={{ mission, items }}
        onHome={() => {
          void flush()
          void queryClient.invalidateQueries({ queryKey: missionKeys.home(userId) })
          void navigate('/')
        }}
      />
    )
  }

  const paused = mission.status === 'PAUSED'
  const pendingPoints = syncState?.pending ?? 0
  const observing = detection !== null && detection.setup.profile.mode !== 'AUTO'
  const labelOf = (itemId: string) => {
    const item = items.find((candidate) => candidate.id === itemId)
    return item ? itemAddress(item.snapshot) : 'Résidence'
  }
  const ambiguityIds = snapshot?.view.ambiguity ?? null
  const ambiguityItems = ambiguityIds
    ? ambiguityIds.flatMap((itemId) => items.filter((item) => item.id === itemId))
    : null
  const following = nextAfter(items, focusId, ordered)
  const focusCard = focus ? (
    <FocusCard
      focus={focus}
      variant={variant}
      observing={observing}
      paused={paused}
      phase={phases[focus.item.id] ?? 'idle'}
      message={messages[focus.item.id]}
      approach={focusApproach}
      onOpen={(item) => {
        setProblemState({ status: 'idle' })
        setSheetProblem(false)
        setSelectedId(item.id)
      }}
      onProblem={(item) => {
        setProblemState({ status: 'idle' })
        setSheetProblem(true)
        setSelectedId(item.id)
      }}
      footer={
        variant === '1b' ? (
          <div className="op-focus__next">
            {following ? (
              <span className="op-panel__next">
                <span className="field-eyebrow">Ensuite</span>
                <span className="op-focus__next-address">{itemAddress(following.snapshot)}</span>
              </span>
            ) : (
              <span className="op-panel__next op-muted">Aucune autre résidence à faire</span>
            )}
            <button
              type="button"
              className="field-button field-button--outline"
              onClick={() => {
                setListOpen(true)
              }}
            >
              <List aria-hidden="true" /> Liste · {counts.waiting}
            </button>
          </div>
        ) : null
      }
      onConfirm={(item) => void complete(item)}
      onShortHold={(item) => {
        setMessage(item.id, { kind: 'short', text: 'Trop court — maintenez 3 secondes.' }, 1800)
      }}
    />
  ) : null

  return (
    <div className="field-app">
      <header className="field-header op-field-header">
        <img src={logo} alt="Groupe RÉCA" className="op-topbar__logo" />
        <span className="field-header__spacer" />
        {detection ? (
          <span className="field-pill" title="Le moteur propose, le maintien de 3 s confirme.">
            Observation
          </span>
        ) : null}
        <GpsIndicator
          state={gps}
          accuracyM={snapshot?.lastFix?.accuracyM ?? null}
          imprecise={snapshot?.view.gps.status === 'SUSPENDED' && snapshot.view.gps.reason === 'INACCURATE'}
          onRetry={() => {
            tracker.retryPermission()
          }}
        />
        {wakeLock === 'refused' || wakeLock === 'unsupported' ? (
          <span className="field-pill field-pill--warn" role="status">
            Veille possible
          </span>
        ) : null}
        {!online ? (
          <span className="field-pill field-pill--warn" role="status">
            <WifiOff aria-hidden="true" /> Hors ligne
          </span>
        ) : pendingPoints > 0 ? (
          <span className="field-pill" role="status">
            {pendingPoints} en attente d’envoi
          </span>
        ) : (
          <span className="field-pill field-pill--ok" role="status">
            En ligne
          </span>
        )}
        {settings.showFocusButton ? (
          <button
            type="button"
            className="field-map-toggle"
            aria-pressed={variant === '1b'}
            aria-label="Vue focus résidence"
            onClick={toggleVariant}
          >
            <Columns2 aria-hidden="true" /> Focus
          </button>
        ) : null}
        {debugAllowed && settings.showDiagButton ? (
          <button
            type="button"
            className="field-map-toggle"
            aria-pressed={showDebug}
            aria-label="Diagnostic de détection"
            onClick={() => {
              toggleDebug(!showDebug)
            }}
          >
            Diag
          </button>
        ) : null}
        {debugAllowed && settings.showDetectionButton ? (
          <button
            type="button"
            className="field-map-toggle"
            aria-pressed={showZones}
            aria-label="Drapeaux et zones de détection"
            onClick={() => {
              toggleZones(!showZones)
            }}
          >
            Détection
          </button>
        ) : null}
        {debugAllowed && settings.showProfileButton ? (
          // Calibration (hors production) : « Profil » remplace « Pause » dans l'en-tête.
          <button
            type="button"
            className="field-button field-button--outline"
            aria-pressed={profileOpen}
            onClick={() => {
              setProfileOpen((value) => !value)
            }}
          >
            <SlidersHorizontal aria-hidden="true" /> Profil
          </button>
        ) : (
          <button type="button" className="field-button field-button--outline" disabled={busy} onClick={() => void togglePause()}>
            {paused ? <Play aria-hidden="true" /> : <Pause aria-hidden="true" />} {paused ? 'Reprendre' : 'Pause'}
          </button>
        )}
        <HelpButton compact />
        <button
          type="button"
          className="field-map-toggle field-map-toggle--icon"
          aria-label="Paramètres"
          aria-pressed={settingsOpen}
          onClick={() => {
            setSettingsOpen(true)
          }}
        >
          <Settings aria-hidden="true" />
        </button>
        <button
          type="button"
          className="field-button field-button--primary"
          onClick={() => {
            setEndOpen(true)
          }}
        >
          <Flag aria-hidden="true" /> Terminer ma sortie
        </button>
      </header>

      <div className="field-body">
        <div className={`field-list op-panel op-panel--${variant}`} aria-label="Résidences de la mission">
          <MissionCard mission={mission} />
          {ambiguityItems ? (
            <AddressChoice
              candidates={ambiguityItems}
              active={
                focus && snapshot?.view.active?.itemId === focus.item.id
                  ? { item: focus.item, observedMs: focus.observedMs ?? 0 }
                  : null
              }
              onChoose={(itemId) => {
                detection?.dispatch({ kind: 'CHOOSE_SITE', itemId })
              }}
              onNone={() => {
                detection?.dispatch({ kind: 'CHOOSE_NONE' })
              }}
            />
          ) : (
            <>
              <FieldBanners
                snapshot={snapshot}
                profile={detection?.setup.profile ?? null}
                observing={observing}
                labelOf={labelOf}
                dismissed={dismissedDecision}
                onDismiss={setDismissedDecision}
                online={online}
                pendingPoints={pendingPoints}
              />
              {claimableOffers > 0 || mission.missionKind === 'REINFORCEMENT' ? (
                <button
                  type="button"
                  className="op-reinforcement-banner"
                  onClick={() => {
                    setReinforcementOpen(true)
                  }}
                >
                  <Handshake aria-hidden="true" />
                  {claimableOffers > 0
                    ? `${String(claimableOffers)} résidence${claimableOffers > 1 ? 's' : ''} offerte${claimableOffers > 1 ? 's' : ''} en renfort`
                    : 'Renfort : aucune résidence offerte pour l’instant'}
                  <span>Voir</span>
                </button>
              ) : null}
              {transferredCount > 0 ? (
                <p className="op-transferred" role="status">
                  {transferredCount} résidence{transferredCount > 1 ? 's' : ''} prise{transferredCount > 1 ? 's' : ''} en
                  charge par une autre mission
                </p>
              ) : null}
              {variant === '1b' && !listOpen ? (
                <>
                  {focusCard ?? (
                    <div className="field-empty op-focus-empty">
                      <strong>Aucune résidence à faire</strong>
                      {counts.problem > 0 ? (
                        <p>Il reste {counts.problem} résidence(s) en problème : voyez la liste.</p>
                      ) : (
                        <p>Vous pouvez terminer votre sortie.</p>
                      )}
                      <button
                        type="button"
                        className="field-button field-button--outline"
                        onClick={() => {
                          setListOpen(true)
                        }}
                      >
                        <List aria-hidden="true" /> Liste
                      </button>
                    </div>
                  )}
                </>
              ) : (
                <>
                  {variant === '1b' ? (
                    <button
                      type="button"
                      className="field-button field-button--outline op-panel__back"
                      onClick={() => {
                        setListOpen(false)
                      }}
                    >
                      <ArrowLeft aria-hidden="true" /> Retour à la résidence
                    </button>
                  ) : (
                    focusCard
                  )}
              <div className="op-sort" role="group" aria-label="Ordre des résidences">
                <button
                  type="button"
                  aria-pressed={sort === 'NEAREST'}
                  onClick={() => {
                    setSort('NEAREST')
                  }}
                >
                  <ArrowDownUp aria-hidden="true" /> Plus proche
                </button>
                <button
                  type="button"
                  aria-pressed={sort === 'ROUTE'}
                  onClick={() => {
                    setSort('ROUTE')
                  }}
                >
                  <RouteIcon aria-hidden="true" /> Ordre de la route
                </button>
              </div>
              <div className="op-filters" role="tablist" aria-label="Filtrer les résidences">
                {filters.map((candidate) => (
                  <button
                    key={candidate.key}
                    type="button"
                    role="tab"
                    aria-selected={filter === candidate.key}
                    className={`op-filter op-filter--${candidate.key.toLowerCase()}`}
                    onClick={() => {
                      setFilter(candidate.key)
                    }}
                  >
                    {candidate.label}{' '}
                    <span>
                      {candidate.key === 'WAITING' ? counts.waiting : candidate.key === 'PROBLEM' ? counts.problem : counts.completed}
                    </span>
                  </button>
                ))}
              </div>
              {visible.length === 0 ? (
                <div className="field-empty">
                  <strong>
                    {filter === 'WAITING'
                      ? 'Aucune résidence à faire'
                      : filter === 'PROBLEM'
                        ? 'Aucun problème en cours'
                        : 'Aucune résidence terminée'}
                  </strong>
                  {filter === 'WAITING' && counts.problem > 0 ? (
                    <p>Il reste {counts.problem} résidence(s) en problème : voyez l’onglet Problèmes.</p>
                  ) : null}
                </div>
              ) : null}
              <div className="field-cards">
                {visible.map((item) => {
                  const phase = phases[item.id] ?? 'idle'
                  const message = messages[item.id]
                  const address = itemAddress(item.snapshot)
                  const lastProblem = [...item.events].reverse().find((event) => event.kind === 'PROBLEM_REPORTED')
                  return (
                    <div
                      key={item.id}
                      className={`field-card${selectedId === item.id ? ' is-selected' : ''}${
                        phase === 'success' ? ' is-success' : ''
                      }${message?.kind === 'error' ? ' is-error' : ''}${item.status === 'PROBLEM' ? ' is-problem' : ''}`}
                    >
                      <div className="field-card__row">
                        <button
                          type="button"
                          className="field-card__open"
                          aria-label={`Ouvrir la fiche du ${address}`}
                          onClick={() => {
                            setProblemState({ status: 'idle' })
                            setSheetProblem(false)
                            setSelectedId(item.id)
                          }}
                        >
                          <span className="field-card__address">
                            <span className="op-order">{item.order}</span> {address}
                          </span>
                          {item.snapshot.clientName ? (
                            <span className="field-card__name">{item.snapshot.clientName}</span>
                          ) : null}
                          <span className="field-card__tags">
                            {item.status === 'PROBLEM' && lastProblem?.reasonCode ? (
                              <span className="field-tag field-tag--attempt">
                                <TriangleAlert aria-hidden="true" /> {fieldProblemReasonLabels[lastProblem.reasonCode]}
                              </span>
                            ) : null}
                            {item.status === 'COMPLETED' && item.completedAt ? (
                              <span className="field-tag">Terminée à {formatTime(item.completedAt)}</span>
                            ) : null}
                            {!hasCoordinates(item.snapshot) ? <span className="field-tag">Sans coordonnées</span> : null}
                          </span>
                        </button>
                        {item.status !== 'COMPLETED' && !paused ? (
                          <HoldButton
                            address={address}
                            phase={phase}
                            onConfirm={() => void complete(item)}
                            onCancel={() => {
                              setMessage(item.id, { kind: 'short', text: 'Trop court — maintenez 3 secondes.' }, 1800)
                            }}
                          />
                        ) : null}
                      </div>
                      {message ? (
                        <div className={`field-card__message field-card__message--${message.kind}`} role="status">
                          {message.text}
                        </div>
                      ) : null}
                    </div>
                  )
                })}
              </div>
                </>
              )}
            </>
          )}
        </div>

        <div className="field-panel">
          <MissionMap
            apiKey={appDependencies.config.googleMapsApiKey}
            mapId={appDependencies.config.googleMapsMapId}
            onRotationChange={setMapRotation}
            items={items}
            selectedId={selectedId}
            hidden={selected !== null}
            operator={gps}
            track={trackSegments?.segments ?? null}
            suggested={suggested}
            {...(appDependencies.config.mapboxToken ? { onToggleSuggested: toggleSuggested } : {})}
            suggestedOn={showSuggested}
            onToggleTrack={toggleTrack}
            trackOn={showTrack}
            streetTarget={streetTarget}
            detection={mapDetection}
            debugFlags={debugFlags}
            stable={stable}
            guide={guide}
            routeColor={mission.routeColor}
            civicSize={settings.civicSize}
            liaison={liaison}
            onOpen={(itemId) => {
              setProblemState({ status: 'idle' })
              setSheetProblem(false)
              setSelectedId(itemId)
            }}
          />
          {profileOpen && view.data ? (
            <ProfileWindow
              serverProfile={serverProfile}
              fallback={demoProfile}
              override={activeOverride}
              onApply={(next) => {
                setOverride(next)
                writeProfileOverride(next)
              }}
              onClose={() => {
                setProfileOpen(false)
              }}
            />
          ) : null}
          {diagnosing && view.data && !selected ? (
            <DetectionDebugPanel
              items={items}
              diagnosis={diagnoseDetection(view.data)}
              detection={detection}
              gps={gps}
              radii={debugRadii}
              radiiLabel={detection ? detection.setup.profile.label : 'référence Démo v0.3 (aucun profil actif)'}
              stable={stable}
              rotation={mapRotation}
              onClose={() => {
                toggleDebug(false)
              }}
            />
          ) : null}
          {showTrack && trackSegments && trackSegments.breaks + trackSegments.discarded.outliers > 0 ? (
            <div className="op-track-note" role="note">
              Trace interrompue {trackSegments.breaks} fois (pauses, interruptions GPS ou écarts) ·{' '}
              {trackSegments.discarded.outliers + trackSegments.discarded.inaccurate} point(s) écarté(s)
            </div>
          ) : null}
          {selected ? (
            <ItemSheet
              key={`${selected.id}:${sheetProblem ? 'problem' : 'sheet'}`}
              item={selected}
              backLabel="Retour à la mission"
              mapsApiKey={appDependencies.config.googleMapsApiKey}
              online={online}
              draft={drafts[selected.id] ?? emptyDraft}
              onDraftChange={(draft) => {
                setDrafts((current) => ({ ...current, [selected.id]: draft }))
              }}
              problemState={problemState}
              canReport={!paused}
              startWithProblem={sheetProblem}
              onBack={() => {
                setSheetProblem(false)
                setSelectedId(null)
              }}
              onReportProblem={(draft) => reportProblem(selected, draft)}
            />
          ) : null}
        </div>
      </div>

      {notice ? (
        <div className="op-toast" role="status">
          {notice}
        </div>
      ) : null}

      {paused ? (
        <div className="op-pause" role="dialog" aria-modal="true" aria-label="Mission en pause">
          <div className="op-card">
            <h2>Mission en pause</h2>
            <p className="op-muted">
              Depuis {mission.pausedAt ? formatTime(mission.pausedAt) : '—'}. La position n’est pas enregistrée pendant
              la pause; la mission et son travail sont conservés.
            </p>
            <button type="button" className="op-start-button" disabled={busy} onClick={() => void togglePause()}>
              <Play aria-hidden="true" /> Reprendre la mission
            </button>
          </div>
        </div>
      ) : null}

      {settingsOpen ? (
        <SettingsDialog
          settings={settings}
          calibrationTools={debugAllowed}
          onChange={changeSettings}
          onClose={() => {
            setSettingsOpen(false)
          }}
        />
      ) : null}
      {reinforcementOpen ? (
        <div className="op-settings" role="dialog" aria-modal="true" aria-label="Renfort disponible">
          <section className="op-settings__card op-settings__card--wide">
            <header className="op-settings__head">
              <h2>Renfort</h2>
              <button
                type="button"
                className="op-settings__close"
                aria-label="Fermer le renfort"
                onClick={() => {
                  setReinforcementOpen(false)
                }}
              >
                <X aria-hidden="true" />
              </button>
            </header>
            <ReinforcementPanel missionId={mission.id} />
          </section>
        </div>
      ) : null}
      {endOpen ? (
        <EndMissionDialog
          mission={mission}
          items={items}
          onClose={() => {
            setEndOpen(false)
          }}
          onEnded={async () => {
            setEndOpen(false)
            tracker.stop(null)
            await flush()
            await view.refetch()
          }}
        />
      ) : null}
    </div>
  )
}
